/**
 * @file Génère un objet JSON-LD de type NewsArticle conforme au standard Schema.org
 * @description Ce module transforme les données d’un article (item)
 *              en un schéma structuré destiné à améliorer le référencement (SEO).
 * @author Roger Sioufi roger.sioufi[at]makeitbloom.com
 *
 */

/**
 * Génère un schéma JSON-LD pour un article de type "NewsArticle".
 *
 * @param item - Données de l’article
 * @param baseUrl - URL de base du site (ex. https://ville.longueuil.quebec)
 * @param locale - Code de langue (fr)
 * @returns {Object|null} Objet JSON-LD ou null si données insuffisantes
 */
export function generateNewsArticleSchema({ item, baseUrl, locale }) {
    if (!item?.title || !item?.created || !item?.details) return null;

    const url = buildFullUrl(baseUrl, locale, item.path); // Construire l'url

    const description = getTruncatedPlainText(item.details.processed, 200); // Nettoie description

    const imageObject = buildImageObject(item.media_image?.meta?.links?.front_large_21_9); // Construire object image

    return {
        '@context': 'https://schema.org',
        '@type': 'NewsArticle',
        headline: item.title,
        datePublished: toISOString(item.created),
        dateModified: toISOString(item.changed || item.created),
        mainEntityOfPage: {
            '@type': 'WebPage',
            '@id': url,
        },
        author: {
            '@type': 'Organization',
            name: 'Ville de Longueuil',
            url: baseUrl,
        },
        publisher: {
            '@type': 'Organization',
            name: 'Ville de Longueuil',
            logo: {
                '@type': 'ImageObject',
                url: `${baseUrl}/logo.png`,
                name: 'Logo de la Ville de Longueuil',
            },
        },
        description,
        ...(imageObject && { image: imageObject }),
    };
}

/**
 * Construit l’URL complète vers l’article à partir du chemin, locale et baseUrl.
 */
function buildFullUrl(baseUrl, locale, path) {
    const cleanPath = typeof path === 'string' ? path : path?.alias || '';
    return new URL(`/${locale}${cleanPath}`, baseUrl).href;
}

/**
 * Supprime les balises HTML et tronque le texte si nécessaire.
 */
function getTruncatedPlainText(html, maxLength) {
    const text = stripHtml(html || '');
    return text.length <= maxLength ? text : text.substring(0, maxLength - 3) + '...';
}

/**
 * Nettoie un contenu HTML en supprimant toutes les balises et espaces superflus.
 */
function stripHtml(html) {
    return html
        .replace(/<\/?[^>]+(>|$)/g, '')
        .replace(/\s+/g, ' ')
        .trim();
}

/**
 * Retourne un objet ImageObject si une URL est fournie, sinon null.
 */
function buildImageObject(imageUrl) {
    return imageUrl
        ? {
              '@type': 'ImageObject',
              url: imageUrl,
          }
        : null;
}

/**
 * Convertit une date JS en chaîne ISO (RFC 3339), attendue par Schema.org.
 */
function toISOString(date) {
    return new Date(date).toISOString();
}
